import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import { errors } from '@codegrade/utils';
import CgError from '@/components/Error';
import Loader from '@/components/Loader';
import * as models from '@/models';
import * as utils from '@/utils';
export default tsx.component({
    props: {
        token: p(String).optional,
    },
    /** @returns The initial state. */
    data() {
        return {
            settings: null,
            error: null,
            loadingSettings: {},
            saveSettingsErrors: {},
            baseId: `notification-settings-${utils.getUniqueId()}`,
        };
    },
    computed: {
        /** @returns Whether we are still loading the settings. */
        loading() {
            return this.settings == null;
        },
        /** @returns The contents of the component. */
        bodyNode() {
            if (this.error != null) {
                return <CgError error={this.error}/>;
            }
            if (this.settings == null) {
                return <Loader scale={1}/>;
            }
            return this.renderSettingsEditor(this.settings);
        },
    },
    methods: {
        /** Load the current settings for the logged in user. */
        async load() {
            this.settings = null;
            this.error = null;
            try {
                this.settings = await models.NotificationSettings.loadFromServer(this.token);
            }
            catch (e) {
                this.error = errors.coerceToError(e);
            }
        },
        /**
         * Update the value of the given setting for the logged in user.
         *
         * @param setting The setting to update.
         * @param newValue The new value to use for the given setting.
         */
        async updateSetting(setting, newValue) {
            utils.vueSet(this.loadingSettings, setting.reason, true);
            try {
                await utils.waitAtLeast(250, setting.updateValue(newValue, this.token));
            }
            catch (caught) {
                const error = errors.coerceToError(caught);
                utils.vueSet(this.saveSettingsErrors, setting.reason, error);
                await this.$nextTick();
            }
            utils.vueSet(this.loadingSettings, setting.reason, false);
        },
        /**
         * Hide the error for the given setting type.
         *
         * @param setting The setting to hide the error for.
         */
        hideError(setting) {
            utils.vueDelete(this.saveSettingsErrors, setting.reason);
        },
        /**
         * Whether to show the select box for the given setting type.
         *
         * @param setting The setting to check.
         * @returns Whether to show the select box for this setting.
         */
        showSelect(setting) {
            const reason = setting.reason;
            return this.saveSettingsErrors[reason] == null && !this.loadingSettings[reason];
        },
        /**
         * Render the select boxes for all settings.
         *
         * @param settings The loaded settings.
         * @returns The settings editor.
         */
        renderSettingsEditor(settings) {
            return [
                <h4>
                    Feedback
                    <cg-description-popover hug-text>
                        With these settings you can determine when you should get an email
                        notification when somebody creates a new comment. The possible options are:
                        <ul class="feedback-notification-options-list">
                            <li>
                                <b>direct</b>: Send an email as soon as a new comment is placed.
                            </li>
                            <li>
                                <b>daily</b>: Send a daily digest email with all new comments. If
                                there are no new comments no email is sent.
                            </li>
                            <li>
                                <b>weekly</b>: Same as the <b>daily</b>, but send a weekly digest.
                            </li>
                            <li>
                                <b>off</b>: Never notify me.
                            </li>
                        </ul>
                    </cg-description-popover>
                </h4>,
                <hr class="mb-2"/>,
                <table class="table table-borderless m-0 border-0">
                    {settings.settings.map(setting => (<tr key={setting.reason}>
                            <td class="w-100">
                                <b-form-group labelFor={`${this.baseId}-${setting.reason}`} class="mb-3">
                                    <template slot="label">
                                        <div class="d-flex align-items-center">
                                            <b-badge class="mr-3">{setting.reason}</b-badge>
                                            <div class="flex-grow-1">
                                                <b-form-select id={`${this.baseId}-${setting.reason}`} options={settings.possibleValues} style={{
                            opacity: this.showSelect(setting) ? 1 : 0,
                        }} value={setting.value} onInput={(event) => this.updateSetting(setting, event)} nativeOnClick={m.stop(() => { })}/>
                                            </div>
                                        </div>
                                    </template>

                                    <template slot="description">
                                        <span class="mt-1 d-block first-letter-caps text-muted">
                                            {setting.explanation}
                                        </span>
                                    </template>
                                </b-form-group>
                            </td>
                        </tr>))}
                </table>,
            ];
        },
    },
    /** Load the settings when the component is created. */
    created() {
        this.load();
    },
    /** @returns The rendered component. */
    render() {
        return <div class="notification-settings">{this.bodyNode}</div>;
    },
});
